import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  formatBrandMoney,
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandLocale,
  getBrandDatePickerLocale,
  getBrandPriceTierThresholds,
} from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { getBrandCountryOptions } from '../../shared/utils/brandCountry';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCheck, Search, Plus, Edit, Trash2, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Users, Phone, Mail, MapPin, TrendingUp, DollarSign, FileText, Building, List, LayoutList, Shield, Tag } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useTranslation } from 'react-i18next';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
// Tables-mobile.css - DELETED (not needed)

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

/**
 * Inline edit cell — MUST stay outside FacturiersPage.
 * Defining this inside the parent makes React see a new component type every render,
 * so each keystroke remounts the input, re-runs focus+select(), and replaces text (blue selection bug).
 */
function FacturierEditableCell({
  recordId,
  field,
  value,
  className = 'text-white font-semibold',
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  editTitle,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSaveRef.current = true;
            onSave(recordId, field, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }


  const facturierFormTabs = useMemo(() => ([
    { id: 'personal', label: p('section_personal_info'), icon: UserCheck, iconColor: 'text-blue-400' },
    { id: 'address', label: p('section_address'), icon: MapPin, iconColor: 'text-purple-400' },
  ]), [p]);

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(recordId, field, value);
        }
      }}
      className={`${className} cursor-pointer`}
      title={editTitle}
    >
      {value || '-'}
    </span>
  );
}

const FACTURIER_MODAL_SHELL = 'bg-white border border-gray-200 backdrop-blur-lg rounded-2xl';
const FACTURIER_MODAL_SHADOW = {
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)',
};
const FACTURIER_FIELD =
  'w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all';
const FACTURIER_LABEL = 'block text-gray-600 text-sm mb-2 font-medium';
const FACTURIER_SECTION = 'bg-gray-50 rounded-xl border border-gray-200 overflow-hidden';

const FacturiersPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.facturiersPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const amountTiers = useMemo(() => {
    const { lowMax, highMin } = getBrandPriceTierThresholds();
    return {
      top: isSaudiBrand() ? highMin * 10 : 100000,
      high: isSaudiBrand() ? highMin : 50000,
      mid: isSaudiBrand() ? lowMax : 10000,
    };
  }, []);
  const cityOptions = useMemo(() => {
    const cities = t('modules.facturation.pages.facturiersPage.default_cities', { returnObjects: true });
    if (cities && typeof cities === 'object' && !Array.isArray(cities)) {
      return Object.entries(cities).map(([value, label]) => ({
        value,
        label,
        isDefault: true,
      }));
    }
    return [
      { value: 'Riyadh', label: 'الرياض', isDefault: true },
      { value: 'Jeddah', label: 'جدة', isDefault: true },
      { value: 'Dammam', label: 'الدمام', isDefault: true },
      { value: 'Makkah', label: 'مكة', isDefault: true },
    ];
  }, [t]);
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const { settings } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [facturiers, setFacturiers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);
  
  // Inline editing states
  const [editingCell, setEditingCell] = useState(null); // { recordId, field }
  const [editValue, setEditValue] = useState('');
  
  const [facturierFormTab, setFacturierFormTab] = useState('personal');
  
  const [formData, setFormData] = useState({ 
    code: '',
    facturier_id: '',
    first_name: '',
    last_name: '',
    status: '',
    total_sales: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    postal_code: '',
    country: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_facturiers');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'المُصدر' });
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadFacturiers(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadFacturiers = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/facturiers');
      const rows = response?.data || response || [];
      setFacturiers(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading facturiers:', error);
      setFacturiers([]);
    }
  };

  const stats = {
    total: facturiers.length,
    active: facturiers.filter(f => f.status === 'active').length,
    inactive: facturiers.filter(f => f.status === 'inactive').length,
    invoiceCount: facturiers.reduce((sum, f) => sum + (parseInt(f.invoice_count) || 0), 0),
    totalInvoices: facturiers.reduce((sum, f) => sum + (parseFloat(f.invoice_total) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_facturiers', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_performance', name: p('tab_by_performance'), icon: TrendingUp },
    { id: 'par_activite', name: p('tab_by_activity'), icon: Users }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: p('filter_inactive'), icon: XCircle },
    { id: 'high_performers', name: p('filter_high_performers'), icon: TrendingUp }
  ];

  // Enhanced distribution with more business insights
  const getPerformanceDistribution = () => {
    const { top, high, mid } = amountTiers;
    const highPerformers = facturiers.filter(f => (parseFloat(f.total_sales) || 0) >= top).length;
    const mediumPerformers = facturiers.filter(f => {
      const v = parseFloat(f.total_sales) || 0;
      return v >= high && v < top;
    }).length;
    const lowPerformers = facturiers.filter(f => {
      const v = parseFloat(f.total_sales) || 0;
      return v >= mid && v < high;
    }).length;
    const newFacturiers = facturiers.filter(f => (parseFloat(f.total_sales) || 0) < mid).length;
    
    return [
      { id: 'high', label: p('dist_high_performance'), count: highPerformers, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { id: 'medium', label: p('dist_medium_performance'), count: mediumPerformers, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
      { id: 'low', label: p('dist_low_performance'), count: lowPerformers, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
      { id: 'new', label: p('dist_new'), count: newFacturiers, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
    ];
  };

  const getActivityDistribution = () => {
    const veryActive = facturiers.filter(f => (parseInt(f.invoice_count) || 0) >= 50).length;
    const active = facturiers.filter(f => (parseInt(f.invoice_count) || 0) >= 20 && (parseInt(f.invoice_count) || 0) < 50).length;
    const moderatelyActive = facturiers.filter(f => (parseInt(f.invoice_count) || 0) >= 5 && (parseInt(f.invoice_count) || 0) < 20).length;
    const lessActive = facturiers.filter(f => (parseInt(f.invoice_count) || 0) > 0 && (parseInt(f.invoice_count) || 0) < 5).length;
    const inactive = facturiers.filter(f => (parseInt(f.invoice_count) || 0) === 0).length;
    
    return [
      { id: 'very_active', label: p('dist_very_active'), count: veryActive, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { id: 'active', label: p('dist_active'), count: active, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
      { id: 'moderate', label: p('dist_moderately_active'), count: moderatelyActive, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
      { id: 'less_active', label: p('dist_less_active'), count: lessActive, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
      { id: 'inactive', label: p('dist_inactive'), count: inactive, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
    ];
  };

  const facturierDistribution = {
    all_facturiers: {
      title: p('dist_overview_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { id: 'inactive', label: p('dist_inactive'), count: stats.inactive, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { id: 'total_invoices', label: p('dist_total_invoices'), count: stats.invoiceCount, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { id: 'high_performance', label: p('dist_high_performance'), count: facturiers.filter(f => (parseFloat(f.total_sales) || 0) >= amountTiers.top).length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { id: 'inactive', label: p('dist_inactive'), count: stats.inactive, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_performance: {
      title: p('dist_by_performance_title'),
      data: getPerformanceDistribution()
    },
    par_activite: {
      title: p('dist_by_activity_title'),
      data: getActivityDistribution()
    }
  };

  const distributionInfo = facturierDistribution[mainViewMode] || facturierDistribution.all_facturiers;

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (row) => row.code),
    textColumnRule('name', (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim()),
    textColumnRule('invoice_count', (row) => String(row.invoice_count ?? '')),
    textColumnRule('invoice_total', (row) => String(row.invoice_total ?? '')),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredFacturiers = useMemo(() => {
    const base = facturiers.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const createdDate = new Date(f.created_at);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (createdDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdDate > endOfDay) return false;
      }
    }
    
    if (searchTerm && !f.code?.toLowerCase().includes(searchTerm.toLowerCase()) && !f.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !f.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !f.email?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_facturiers') {
      if (quickFilter === 'active' && f.status !== 'active') return false;
      if (quickFilter === 'inactive' && f.status !== 'inactive') return false;
      if (quickFilter === 'high_performers' && (parseFloat(f.invoice_total) || 0) < amountTiers.top) return false;
    }
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_facturiers':
        case 'par_statut':
          if (activeDistributionFilter === 'active' && f.status !== 'active') return false;
          if (activeDistributionFilter === 'inactive' && f.status !== 'inactive') return false;
          if (activeDistributionFilter === 'high_performance' && (parseFloat(f.total_sales) || 0) < amountTiers.top) return false;
          break;
        case 'par_performance':
          if (activeDistributionFilter === 'high' && (parseFloat(f.total_sales) || 0) < amountTiers.top) return false;
          if (activeDistributionFilter === 'medium' && ((parseFloat(f.total_sales) || 0) < amountTiers.high || (parseFloat(f.total_sales) || 0) >= amountTiers.top)) return false;
          if (activeDistributionFilter === 'low' && ((parseFloat(f.total_sales) || 0) < amountTiers.mid || (parseFloat(f.total_sales) || 0) >= amountTiers.high)) return false;
          if (activeDistributionFilter === 'new' && (parseFloat(f.total_sales) || 0) >= amountTiers.mid) return false;
          break;
        case 'par_activite':
          if (activeDistributionFilter === 'very_active' && (parseInt(f.invoice_count) || 0) < 50) return false;
          if (activeDistributionFilter === 'active' && ((parseInt(f.invoice_count) || 0) < 20 || (parseInt(f.invoice_count) || 0) >= 50)) return false;
          if (activeDistributionFilter === 'moderate' && ((parseInt(f.invoice_count) || 0) < 5 || (parseInt(f.invoice_count) || 0) >= 20)) return false;
          if (activeDistributionFilter === 'less_active' && ((parseInt(f.invoice_count) || 0) === 0 || (parseInt(f.invoice_count) || 0) >= 5)) return false;
          if (activeDistributionFilter === 'inactive' && (parseInt(f.invoice_count) || 0) !== 0) return false;
          break;
      }
    }
    return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [facturiers, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, filters, columnFilterRules]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingRecord) {
        await localApiAdapter.put(`/facturation/facturiers/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/facturation/facturiers', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('notify_save_error'));
    } finally {
      resetForm();
      loadFacturiers();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setFacturierFormTab('personal');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/facturation/facturiers/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadFacturiers();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('notify_save_error'));
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/facturiers/${id}`, { status: newStatus });
      loadFacturiers();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Inline editing functions
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    // Don't save if value hasn't changed or is being cleared intentionally
    const record = facturiers.find(f => f.id === recordId);
    if (!record) {
      cancelEditing();
      return;
    }

    // Get original value
    let originalValue = field === 'name' 
      ? `${record.first_name} ${record.last_name}` 
      : record[field];
    
    // If value is empty or unchanged, just cancel editing
    if (value === originalValue || !value.trim()) {
      cancelEditing();
      return;
    }

    try {
      // Handle name field specially (split into first_name and last_name)
      let updateData = {};
      if (field === 'name') {
        const names = value.trim().split(' ');
        updateData = {
          first_name: names[0] || '',
          last_name: names.slice(1).join(' ') || ''
        };
      } else {
        updateData = { [field]: value };
      }

      await localApiAdapter.put(`/facturation/facturiers/${recordId}`, updateData);
      await loadFacturiers();
      cancelEditing();
      notifyUpdated(showNotify, entityLabel);
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
      notifyError(showNotify, p('notify_inline_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/facturation/facturiers/${id}`)));
        setSelectedRecords([]);
        loadFacturiers();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      code: '', facturier_id: '', first_name: '', last_name: '', status: '', total_sales: '',
      email: '', phone: '', mobile: '', address: '', city: '', postal_code: '', country: ''
    });
    setEditingRecord(null);
    setShowModal(false);
    setFacturierFormTab('personal');
  };

  const getExportHeaders = () => [
      { key: 'code', label: p('export_col_code') },
      { key: 'first_name', label: p('export_col_first_name') },
      { key: 'last_name', label: p('export_col_last_name') },
      { key: 'email', label: p('export_col_email') },
      { key: 'phone', label: p('export_col_phone') },
      { key: 'mobile', label: p('export_col_mobile') },
      { key: 'address', label: p('export_col_address') },
      { key: 'city', label: p('export_col_city') },
      { key: 'postal_code', label: p('export_col_postal_code') },
      { key: 'country', label: p('export_col_country') },
      { key: 'status', label: p('export_col_status') },
      { key: 'total_sales', label: p('export_col_total_sales') }
    ];

  // Export handlers
  const handleExportCSV = () => {
    const headers = getExportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? facturiers.filter(f => selectedRecords.includes(f.id))
      : filteredFacturiers;
    
    exportToCSV(dataToExport, headers, `facturiers_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const headers = getExportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? facturiers.filter(f => selectedRecords.includes(f.id))
      : filteredFacturiers;
    
    await exportToExcel(dataToExport, headers, `facturiers_${new Date().toISOString().split('T')[0]}.xlsx`, p('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0
        ? facturiers.filter(f => selectedRecords.includes(f.id))
        : filteredFacturiers;

      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: p('export_col_code_short'), value: (f) => f.code },
          { label: p('export_col_name'), value: (f) => f.last_name },
          { label: p('export_col_first_name'), value: (f) => f.first_name },
          { label: p('export_col_email'), value: (f) => f.email },
          { label: p('export_col_phone'), value: (f) => f.phone || f.mobile },
          { label: p('export_col_city'), value: (f) => f.city },
          { label: p('export_col_status'), value: (f) => f.status },
          {
            label: p('export_col_revenue_da'),
            align: 'right',
            value: (f) =>
              f.total_sales != null && f.total_sales !== ''
                ? formatBrandMoney(Number(f.total_sales))
                : '',
          },
        ],
        listExportTitle: p('export_list_title_upper'),
        clientTitle: p('export_list_title'),
        printDocumentTitle: p('export_document_title', { date: new Date().toISOString().split('T')[0] }),
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('error_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Import handlers
  const handleImportCSV = () => {
    const headers = getExportHeaders().filter(h => h.key !== 'total_sales');

    const schema = {
      requiredFields: ['first_name', 'last_name'],
      validators: {
        email: (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(p('import_errors', { valid: result.validCount, invalid: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(p('import_success', { count: result.validCount }));
        }
        
        // Add imported data to the list
        if (result.data.length > 0) {
          setFacturiers([...facturiers, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx,
            total_sales: item.total_sales || 0
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_file_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    const headers = getExportHeaders().filter(h => h.key !== 'total_sales');

    const schema = {
      requiredFields: ['first_name', 'last_name'],
      validators: {
        email: (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(p('import_errors', { valid: result.validCount, invalid: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(p('import_success', { count: result.validCount }));
        }
        
        // Add imported data to the list
        if (result.data.length > 0) {
          setFacturiers([...facturiers, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx,
            total_sales: item.total_sales || 0
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_file_error'));
      }
    }, '.xlsx,.xls');
  };

  // Export JSON
  const handleExportJSON = () => {
    const dataToExport = selectedRecords.length > 0 
      ? facturiers.filter(f => selectedRecords.includes(f.id))
      : filteredFacturiers;
    
    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `facturiers_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Export XML
  const handleExportXML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? facturiers.filter(f => selectedRecords.includes(f.id))
      : filteredFacturiers;
    
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<facturiers>\n';
    dataToExport.forEach(item => {
      xmlContent += '  <facturier>\n';
      Object.keys(item).forEach(key => {
        const value = item[key] || '';
        xmlContent += `    <${key}>${value}</${key}>\n`;
      });
      xmlContent += '  </facturier>\n';
    });
    xmlContent += '</facturiers>';
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `facturiers_${new Date().toISOString().split('T')[0]}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Export HTML
  const handleExportHTML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? facturiers.filter(f => selectedRecords.includes(f.id))
      : filteredFacturiers;
    
    let htmlContent = `<!DOCTYPE html>
<html lang="${dateLocale.startsWith('ar') ? 'ar' : 'fr'}">
<head>
  <meta charset="UTF-8">
  <title>${p('export_list_title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1e40af; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #3b82f6; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
  </style>
</head>
<body>
  <h1>${p('export_list_title')}</h1>
  <p>${p('export_date_label')}: ${new Date().toLocaleDateString(dateLocale)}</p>
  <p>${p('export_total_label')}: ${dataToExport.length}</p>
  <table>
    <thead>
      <tr>
        <th>${p('export_col_code_short')}</th>
        <th>${p('export_col_name')}</th>
        <th>${p('export_col_email')}</th>
        <th>${p('export_col_phone')}</th>
        <th>${p('export_col_city')}</th>
        <th>${p('export_col_status')}</th>
      </tr>
    </thead>
    <tbody>`;
    
    dataToExport.forEach(f => {
      htmlContent += `
      <tr>
        <td>${f.code || ''}</td>
        <td>${f.first_name || ''} ${f.last_name || ''}</td>
        <td>${f.email || ''}</td>
        <td>${f.phone || ''}</td>
        <td>${f.city || ''}</td>
        <td>${f.status === 'active' || f.status === 'actif' ? p('status_active') : p('status_inactive')}</td>
      </tr>`;
    });
    
    htmlContent += `
    </tbody>
  </table>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `facturiers_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Export TXT
  const handleExportTXT = () => {
    const dataToExport = selectedRecords.length > 0 
      ? facturiers.filter(f => selectedRecords.includes(f.id))
      : filteredFacturiers;
    
    let txtContent = `${p('export_list_title_upper')}\n${p('export_date_label')}: ${new Date().toLocaleDateString(dateLocale)}\n${p('export_total_label')}: ${dataToExport.length}\n\n`;
    txtContent += '='.repeat(100) + '\n\n';
    
    dataToExport.forEach(f => {
      txtContent += `${p('export_col_code_short')}: ${f.code || ''}\n`;
      txtContent += `${p('export_col_name')}: ${f.first_name || ''} ${f.last_name || ''}\n`;
      txtContent += `${p('export_col_email')}: ${f.email || ''}\n`;
      txtContent += `${p('export_col_phone')}: ${f.phone || ''}\n`;
      txtContent += `${p('export_col_city')}: ${f.city || ''}\n`;
      txtContent += `${p('export_col_status')}: ${f.status === 'active' || f.status === 'actif' ? p('status_active') : p('status_inactive')}\n`;
      txtContent += '-'.repeat(100) + '\n\n';
    });
    
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `facturiers_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Import JSON
  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        
        if (!Array.isArray(data)) {
          alert(p('import_json_invalid'));
          return;
        }
        
        const importedFacturiers = data.map((item, idx) => ({
          ...item,
          id: Date.now() + idx,
          total_sales: item.total_sales || 0
        }));
        
        setFacturiers([...facturiers, ...importedFacturiers]);
        alert(p('import_success', { count: importedFacturiers.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('JSON import error:', error);
        alert(p('import_json_error'));
      }
    }, '.json');
  };

  // Import XML
  const handleImportXML = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        
        const facturiersNodes = xmlDoc.getElementsByTagName('facturier');
        const importedFacturiers = [];
        
        for (let i = 0; i < facturiersNodes.length; i++) {
          const node = facturiersNodes[i];
          const facturier = {
            id: Date.now() + i,
            code: node.getElementsByTagName('code')[0]?.textContent || '',
            first_name: node.getElementsByTagName('first_name')[0]?.textContent || '',
            last_name: node.getElementsByTagName('last_name')[0]?.textContent || '',
            email: node.getElementsByTagName('email')[0]?.textContent || '',
            phone: node.getElementsByTagName('phone')[0]?.textContent || '',
            mobile: node.getElementsByTagName('mobile')[0]?.textContent || '',
            address: node.getElementsByTagName('address')[0]?.textContent || '',
            city: node.getElementsByTagName('city')[0]?.textContent || '',
            postal_code: node.getElementsByTagName('postal_code')[0]?.textContent || '',
            country: node.getElementsByTagName('country')[0]?.textContent || '',
            status: node.getElementsByTagName('status')[0]?.textContent || 'actif',
            total_sales: parseFloat(node.getElementsByTagName('total_sales')[0]?.textContent || '0')
          };
          importedFacturiers.push(facturier);
        }
        
        setFacturiers([...facturiers, ...importedFacturiers]);
        alert(p('import_success', { count: importedFacturiers.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('XML import error:', error);
        alert(p('import_xml_error'));
      }
    }, '.xml');
  };

  // Import TXT/TSV
  const handleImportTXT = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const lines = content.split('\n').filter(line => line.trim());
        
        // Detect delimiter (tab or comma)
        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        
        // Parse header
        const headers = lines[0].split(delimiter).map(h => h.trim());
        
        // Parse data
        const importedFacturiers = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(delimiter);
          const facturier = { id: Date.now() + i, total_sales: 0 };
          
          headers.forEach((header, index) => {
            facturier[header.toLowerCase().replace(/\s+/g, '_')] = values[index]?.trim() || '';
          });
          
          importedFacturiers.push(facturier);
        }
        
        setFacturiers([...facturiers, ...importedFacturiers]);
        alert(p('import_success', { count: importedFacturiers.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('TXT import error:', error);
        alert(p('import_txt_error'));
      }
    }, '.txt,.tsv');
  };

  const generateFacturierNumber = () => {
    const maxNumber = facturiers.reduce((max, f) => {
      if (f.code && f.code.startsWith(`FAC-`)) {
        const num = parseInt(f.code.split('-')[1]);
        return Math.max(max, isNaN(num) ? 0 : num);
      }
      return max;
    }, 0);
    return `FAC-${String(maxNumber + 1).padStart(3, '0')}`;
  };

  const openNewFacturierModal = () => {
    const newCode = generateFacturierNumber();
    setFormData({ 
      code: newCode, facturier_id: '', first_name: '', last_name: '', status: '', total_sales: '',
      email: '', phone: '', mobile: '', address: '', city: '', postal_code: '', country: ''
    });
    setEditingRecord(null);
    setFacturierFormTab('personal');
    setShowModal(true);
  };

  
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredFacturiers);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredFacturiers.map((r) => r.id), [filteredFacturiers]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style} = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: Users, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'inactive', title: p('stat_inactive'), value: stats.inactive, icon: XCircle, color: 'from-red-500 to-red-600' },
    { key: 'invoices', title: p('stat_invoice_count'), value: stats.invoiceCount, icon: FileText, color: 'from-purple-500 to-purple-600' },
    { key: 'revenue', title: p('stat_revenue'), value: formatBrandMoney(stats.totalInvoices), icon: DollarSign, color: 'from-orange-500 to-orange-600', className: 'max-lg:hidden' },
  ], [stats, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_active') || p('status_values.active') || p('dist_active') },
    { value: 'inactive', label: p('status_inactive') || p('status_values.inactive') || p('dist_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? (p('status') || p('status_label') || p('col_status'))
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'code', label: p('col_code'), required: true },
    { key: 'name', label: p('col_full_name') },
    { key: 'invoice_count', label: p('col_invoice_count') },
    { key: 'invoice_total', label: p('col_revenue') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 3 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <div className={`space-y-3 employees-container`}>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={UserCheck}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openNewFacturierModal}
      createLabel={p('new_facturier')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
<div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_facturiers' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.id} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.id ? null : item.id)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.id ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.id ? 'text-white' : 'text-white/80'}`}>{item.label}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.id ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
              />
              
              <div className="relative" ref={statusFilterRef}>
                <button 
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">
                    {statusFilter === 'all' ? p('status_label') : statusFilter === 'active' ? p('status_active') : p('status_inactive')}
                  </span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', 
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <Filter size={16} className="text-white/50" />
                        <span className="text-sm font-medium">{p('all_statuses')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('active');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'active' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <CheckCircle size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{p('status_active')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('inactive');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'inactive' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <XCircle size={16} className="text-red-400" />
                        <span className="text-sm font-medium">{p('status_inactive')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="relative" ref={dateRangeRef}>
                <button 
                  onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setShowImportDropdown(false); setShowExportDropdown(false); }} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('period')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('start_date')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({...dateRange, start: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                    {date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                  </div>
                                  <button
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('end_date')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({...dateRange, end: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select')}
                              minDate={dateRange.start}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                    {date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                                  </div>
                                  <button
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-blue-400/30">
                          <button onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-blue-500/20 text-white/80 hover:text-white rounded-xl text-sm transition-all font-medium">{p('reset')}</button>
                          <button onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl text-sm transition-all font-medium shadow-lg shadow-blue-500/30">{p('apply')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedRecords.length > 0 && (
                <button onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium">
                  <Trash2 size={16} /><span className="hidden sm:inline">{p('delete_with_count', { count: selectedRecords.length })}</span>
                </button>
              )}
              
              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Upload size={16} /><span className="text-sm font-medium">{p('import')}</span><ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileOutput size={16} className="text-purple-400" />
                        <span className="text-sm font-medium">{t('common.format_csv')}</span>
                      </button>
                      <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileSpreadsheet size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{t('common.format_excel')}</span>
                      </button>
                      <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-yellow-400" />
                        <span className="text-sm font-medium">JSON</span>
                      </button>
                      <button onClick={handleImportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-orange-400" />
                        <span className="text-sm font-medium">XML</span>
                      </button>
                      <button onClick={handleImportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-gray-400" />
                        <span className="text-sm font-medium">TXT/TSV</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Download size={16} /><span className="text-sm font-medium">{p('export')}</span><ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50">
                        <FileText size={16} className="text-red-400" />
                        <span className="text-sm font-medium">{isGeneratingPDF ? p('generating') : p('pdf')}</span>
                      </button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileSpreadsheet size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{t('common.format_excel')}</span>
                      </button>
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileOutput size={16} className="text-purple-400" />
                        <span className="text-sm font-medium">{t('common.format_csv')}</span>
                      </button>
                      <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-yellow-400" />
                        <span className="text-sm font-medium">JSON</span>
                      </button>
                      <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-orange-400" />
                        <span className="text-sm font-medium">XML</span>
                      </button>
                      <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-blue-300" />
                        <span className="text-sm font-medium">HTML</span>
                      </button>
                      <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-gray-400" />
                        <span className="text-sm font-medium">{t('common.format_txt')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('code') ? <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={filters.code || ''} onChange={(v) => setFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('name') ? <TableColumnHeaderFilter columnKey="name" label={p('col_full_name')} value={filters.name || ''} onChange={(v) => setFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_email')}</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_phone')}</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_city')}</th>
                  {showCol('invoice_count') ? <TableColumnHeaderFilter columnKey="invoice_count" label={p('col_invoice_count')} value={filters.invoice_count || ''} onChange={(v) => setFilter('invoice_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('invoice_total') ? <TableColumnHeaderFilter columnKey="invoice_total" label={p('col_revenue')} value={filters.invoice_total || ''} onChange={(v) => setFilter('invoice_total', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={[{ value: 'active', label: p('status_active') }, { value: 'inactive', label: p('status_inactive') }]} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredFacturiers.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      {showCol('code') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.code}</span></td>
                      ) : null}
                      {showCol('name') ? (
                      <td className="px-6 py-4">
                        <FacturierEditableCell
                          recordId={record.id}
                          field="name"
                          value={`${record.first_name} ${record.last_name}`}
                          className="text-white font-semibold"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                        />
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <FacturierEditableCell
                          recordId={record.id}
                          field="email"
                          value={record.email}
                          className="text-white/70 text-sm"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'email'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <FacturierEditableCell
                            recordId={record.id}
                            field="phone"
                            value={record.phone}
                            className="text-white font-semibold text-sm"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'phone'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                          />
                          {record.mobile && (
                            <FacturierEditableCell
                              recordId={record.id}
                              field="mobile"
                              value={record.mobile}
                              className="text-white/50 text-xs mt-0.5"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'mobile'}
                              editValue={editValue}
                              onEditValueChange={setEditValue}
                              onStartEdit={startEditing}
                              onSave={saveInlineEdit}
                              onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <FacturierEditableCell
                          recordId={record.id}
                          field="city"
                          value={record.city}
                          className="text-white/70 text-sm"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'city'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                        />
                      </td>
                      {showCol('invoice_count') ? (
                      <td className="px-6 py-4"><span className="text-white font-bold text-lg">{record.invoice_count || 0}</span></td>
                      ) : null}
                      {showCol('invoice_total') ? (
                      <td className="px-6 py-4"><span className="text-white font-bold text-lg">{formatBrandMoney(record.invoice_total || 0)}</span></td>
                      ) : null}
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view') || p('view') || t('common.view')}
                          editTitle={p('action_edit') || p('edit') || t('common.edit')}
                          deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><UserCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_facturiers_total', { count: filteredFacturiers.length })}</span>
                      <span>{p('footer_invoice_count', { count: filteredFacturiers.reduce((sum, f) => sum + (parseInt(f.invoice_count) || 0), 0) })}</span>
                      <span>{p('footer_total_revenue', { amount: formatBrandMoney(filteredFacturiers.reduce((sum, f) => sum + (parseFloat(f.invoice_total) || parseFloat(f.total_sales) || 0), 0)) })}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredFacturiers.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><UserCheck size={20} className="text-emerald-400" />{record.first_name} {record.last_name}</h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_personal_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_code')}:</span> {record.code}</div>
                            <div><span className="font-medium">{p('export_col_first_name')}:</span> {record.first_name}</div>
                            <div><span className="font-medium">{p('export_col_last_name')}:</span> {record.last_name}</div>
                            <div><span className="font-medium">{p('col_status')}:</span> {record.status === 'active' ? p('status_active') : record.status === 'inactive' ? p('status_inactive') : record.status || '—'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_contact')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><Mail size={14} className="text-blue-400 shrink-0" /><span className="font-medium">{p('col_email')}:</span> {record.email || '—'}</div>
                            <div className="flex items-center gap-2"><Phone size={14} className="text-green-400 shrink-0" /><span className="font-medium">{p('col_phone')}:</span> {record.phone || '—'}</div>
                            <div className="flex items-center gap-2"><Phone size={14} className="text-emerald-400 shrink-0" /><span className="font-medium">{p('label_mobile')}:</span> {record.mobile || '—'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_address')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-start gap-2"><MapPin size={14} className="text-amber-400 shrink-0 mt-0.5" /><span><span className="font-medium">{p('label_address')}:</span> {record.address || '—'}</span></div>
                            <div><span className="font-medium">{p('col_city')}:</span> {record.city || '—'}</div>
                            <div><span className="font-medium">{p('label_postal_code')}:</span> {record.postal_code || '—'}</div>
                            <div><span className="font-medium">{p('label_country')}:</span> {record.country || '—'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_performance')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('label_invoice_count')}:</span>
                              <p className="text-white font-bold text-lg mt-1">{record.invoice_count || 0}</p>
                            </div>
                            <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                              <span className="font-medium text-green-300 text-xs">{p('label_revenue')}:</span>
                              <p className="text-green-200 font-bold text-sm mt-1">{formatBrandMoney(record.invoice_total || record.total_sales || 0)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('action_view')}
                      editTitle={p('action_edit')}
                      deleteTitle={p('action_delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><UserCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('view_title')}
          icon={UserCheck}
          maxWidth="1100px"
          testId="facturier-view-modal"
        >
              <div className="space-y-4">
                <div className={`${FACTURIER_SECTION} p-5`}>
                  <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2"><UserCheck size={18} className="text-emerald-500" />{p('section_personal')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-gray-500 text-sm mb-1">{p('col_code')}</p><p className="text-gray-900 font-medium">{viewingRecord.code}</p></div>
                    <div><p className="text-gray-500 text-sm mb-1">{p('export_col_first_name')}</p><p className="text-gray-900 font-medium">{viewingRecord.first_name}</p></div>
                    <div><p className="text-gray-500 text-sm mb-1">{p('export_col_last_name')}</p><p className="text-gray-900 font-medium">{viewingRecord.last_name}</p></div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">{p('label_status')}</p>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${viewingRecord.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {viewingRecord.status === 'active' ? p('status_active') : p('status_inactive')}
                      </span>
                    </div>
                    <div><p className="text-gray-500 text-sm mb-1">{p('label_total_sales')}</p><p className="text-gray-900 font-medium">{formatBrandMoney(viewingRecord.total_sales || 0)}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`${FACTURIER_SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4">{p('section_contact_address')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-gray-500 text-sm mb-1">{p('col_email')}</p><p className="text-gray-900 font-medium">{viewingRecord.email || '-'}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('col_phone')}</p><p className="text-gray-900 font-medium">{viewingRecord.phone || '-'}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_mobile')}</p><p className="text-gray-900 font-medium">{viewingRecord.mobile || '-'}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_address')}</p><p className="text-gray-900 font-medium">{viewingRecord.address || '-'}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_city_country')}</p><p className="text-gray-900 font-medium">{viewingRecord.city || '-'}, {viewingRecord.country || '-'}</p></div>
                    </div>
                  </div>
                  <div className={`${FACTURIER_SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp size={18} className="text-blue-500" />
                      {p('section_performance')}
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <p className="text-gray-500 text-sm mb-1">{p('label_invoice_count')}</p>
                        <p className="text-gray-900 font-bold text-2xl">{viewingRecord.invoice_count || 0}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <p className="text-gray-500 text-sm mb-1">{p('label_revenue')}</p>
                        <p className="text-emerald-600 font-bold text-2xl">{formatBrandMoney(viewingRecord.invoice_total || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('edit_title') : p('create_title')}
          icon={UserCheck}
          formId="facturier-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          onSubmit
          testId="facturier-crud-modal"
          tabs={facturierFormTabs}
          activeTab={facturierFormTab}
          onTabChange={setFacturierFormTab}
        >
              <form id="facturier-form" onSubmit={handleSubmit}>
                {facturierFormTab === 'personal' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code')}</label>
                            <input type="text" required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_code')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_first_name')}</label>
                            <input type="text" required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_first_name')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_last_name')}</label>
                            <input type="text" required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_last_name')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'active', label: p('status_active'), isDefault: true },
                                { value: 'inactive', label: p('status_inactive'), isDefault: true }
                              ]}
                              placeholder={p('placeholder_status')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_facturier_id')}</label>
                            <input type="text" value={formData.facturier_id} onChange={(e) => setFormData({ ...formData, facturier_id: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_internal_ref')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_email')}</label>
                            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_email')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_phone')}</label>
                            <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_phone')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_mobile')}</label>
                            <input type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_mobile')} />
                          </div>
                        </div>
                )}

                {facturierFormTab === 'address' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_address')}</label>
                            <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_address')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_city')}</label>
                            <CustomSelect
                              value={formData.city}
                              onChange={(value) => setFormData({ ...formData, city: value })}
                              options={cityOptions}
                              placeholder={p('placeholder_city')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_postal_code')}</label>
                            <input type="text" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_postal')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_country')}</label>
                            <CustomSelect
                              value={formData.country}
                              onChange={(value) => setFormData({ ...formData, country: value })}
                              options={getBrandCountryOptions()}
                              placeholder={p('placeholder_country')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_sales_da')}</label>
                            <CustomNumberInput step={0.01} value={formData.total_sales || ''} onChange={(value) => setFormData({ ...formData, total_sales: value })} placeholder="0.00" />
                          </div>
                          <div className="flex items-end">
                            <p className="text-white/50 text-sm pb-2.5">{p('help_total_sales')}</p>
                          </div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}
    </div>
  );
};

export default FacturiersPage;


